import { useEffect, useRef, useState } from 'react';
import { useLatest, useMemoizedFn } from 'ahooks';
import { useMutation, useQueryClient } from '@zg-rentals/react-query5';
import { FileUploadStatus, ZDocsUploadManager } from '@zillow/zdocs-web';
import type { ZDocsUpload } from '@zillow/zdocs-web';
import { DatarouterError } from '@zg-rentals/web-base';
import type { ApplicationConfigurationRequiredDocumentType } from '@zg-rentals/ts-capps-client';
import {
  useCappsRenterDocumentsWebApiHandler_addIncomeDocuments,
  useCappsRenterDocumentsWebApiHandler_requestUserToken,
} from '@zg-rentals/ts-capps-client-react-query';
import type { IncomeDocumentsAnswers } from '../utils/incomeDocuments';
import { DOCUMENT_POOL_TAG } from '../constants/applicationsConstants';
import { getMaxDocumentsPerIncome } from '../constants/form';
import { useIsBaseApplicationChangesEnabled } from '../utils/ab-tests';
import { isRequiredSection } from './useRequiredDocumentSections';
import { useAddRequiredDocuments, useAttachRequiredDocuments } from './useRequiredDocumentMutations';
import { RENTAL_APPLICATION_TAG, fetchLatestApplication } from './useRentalApplication';

export const INCOME_DOCUMENT_UPLOAD_KEY = ['incomeDocumentUpload'];

export const INCOME_DOCUMENT_UPLOAD_SCOPE = { id: 'incomeDocumentUpload' };

export const MAX_UPLOAD_SIZE_BYTES = 100 * 1024 * 1024;

export const ACCEPTED_FILE_EXTENSIONS = ['.pdf', '.jpeg', '.jpg', '.png'];

export const STALLED_UPLOAD_MS = 60_000;

const ACCEPTED_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

function isAcceptedFile(file: File) {
  const name = file.name.toLowerCase();
  return (
    ACCEPTED_MIME_TYPES.includes(file.type) || ACCEPTED_FILE_EXTENSIONS.some((extension) => name.endsWith(extension))
  );
}

/** Where an upload goes. The file of an income target is saved to that income source */
export type UploadTarget =
  | { kind: 'supporting'; incomeId: string }
  | { kind: 'incomeRow'; incomeId: string; documentType: ApplicationConfigurationRequiredDocumentType }
  | { kind: 'applicationRow'; documentType: ApplicationConfigurationRequiredDocumentType };

export type PendingUpload = {
  key: string;
  target: UploadTarget;
  fileName: string;
  savedIds?: Array<string>;
};

export type FailedUploadReason = 'type' | 'tooLarge' | 'limit' | 'upload' | 'save' | 'attach' | 'rejected';

export type FailedUpload = {
  key: string;
  target: UploadTarget;
  fileName: string;
  file: File;
  reason: FailedUploadReason;
  canRetry: boolean;
  /** For an 'attach' failure: the saved file that the row does not list */
  documentId?: string;
};

type FailureDetails = Pick<FailedUpload, 'canRetry' | 'documentId'>;

type IncomeRowTarget = Extract<UploadTarget, { kind: 'incomeRow' }>;
type RowTarget = Exclude<UploadTarget, { kind: 'supporting' }>;
type Entry = { key: string; file: File; fileName: string };
type UploadAnswers = IncomeDocumentsAnswers | null | undefined;

type UploadedFiles = Awaited<ReturnType<ZDocsUploadManager['uploadFiles']>>;

let nextKey = 0;

function getIncomeId(target: UploadTarget) {
  return target.kind === 'applicationRow' ? undefined : target.incomeId;
}

function getIds(documents: Array<{ id?: string | null } | null> | null | undefined) {
  return new Set((documents ?? []).flatMap((document) => (document?.id ? [document.id] : [])));
}

function findSection(answers: UploadAnswers, target: RowTarget) {
  const incomeId = getIncomeId(target);
  return (answers?.requiredDocuments ?? []).find(
    (section) => section?.documentType === target.documentType && (section.incomeId || undefined) === incomeId,
  );
}

function getContainerIds(answers: UploadAnswers, incomeId: string) {
  return getIds((answers?.currentIncomes ?? []).find((income) => income?.id === incomeId)?.documents);
}

function getSectionIds(answers: UploadAnswers, target: RowTarget) {
  return getIds(findSection(answers, target)?.documents);
}

// Where the answers must list a saved file before its pending card can go
function getListedIds(answers: UploadAnswers, target: UploadTarget) {
  return target.kind === 'supporting' ? getContainerIds(answers, target.incomeId) : getSectionIds(answers, target);
}

// Where capps stores the file when the save call answers
function getStoredIds(answers: UploadAnswers, target: UploadTarget) {
  return target.kind === 'applicationRow' ? getSectionIds(answers, target) : getContainerIds(answers, target.incomeId);
}

function isListed(answers: UploadAnswers, { target, savedIds }: PendingUpload) {
  const listedIds = getListedIds(answers, target);
  return !!savedIds && savedIds.every((id) => listedIds.has(id));
}

/**
 * What the answers say about a failed attach: 'stored' when the row lists the file, 'gone' when the row is no
 * longer requested or the source no longer has the file, else 'failed'
 */
function getAttachOutcome(answers: UploadAnswers, target: IncomeRowTarget, documentId: string) {
  if (getSectionIds(answers, target).has(documentId)) {
    return 'stored';
  }
  const section = findSection(answers, target);
  if (!section || !isRequiredSection(section) || !getContainerIds(answers, target.incomeId).has(documentId)) {
    return 'gone';
  }
  return 'failed';
}

// zDocs refuses the file itself, so the same bytes fail again. A missing result, HTTP 0, 401, 403, 408 and 5xx can pass
function isRefusedByZDocs({ status, statusCode }: ZDocsUpload) {
  return status === FileUploadStatus.Rejected || statusCode === 200 || statusCode === 400;
}

function isOpenAttachFailure(answers: UploadAnswers, { reason, target, documentId }: FailedUpload) {
  return (
    reason !== 'attach' ||
    target.kind !== 'incomeRow' ||
    !documentId ||
    !answers ||
    getAttachOutcome(answers, target, documentId) === 'failed'
  );
}

/** Ids saved to their source for a row upload that the row does not list yet. Supporting must leave them out */
export function getRowUploadIds(pending: Array<PendingUpload>, failed: Array<FailedUpload>) {
  return new Set([
    ...pending.flatMap(({ target, savedIds }) => (target.kind === 'incomeRow' ? (savedIds ?? []) : [])),
    ...failed.flatMap(({ reason, documentId }) => (reason === 'attach' && documentId ? [documentId] : [])),
  ]);
}

// A saved file counts from the moment its save returns, before the refetched application lists it
function getHeldDocumentCount(answers: UploadAnswers, pending: Array<PendingUpload>, incomeId: string) {
  const ids = getContainerIds(answers, incomeId);
  let uploading = 0;
  for (const entry of pending) {
    if (getIncomeId(entry.target) !== incomeId) {
      continue;
    }
    if (entry.savedIds) {
      entry.savedIds.forEach((id) => ids.add(id));
    } else {
      uploading += 1;
    }
  }
  return ids.size + uploading;
}

/**
 * Uploads income documents for the Income documents step through one zDocs upload manager. A row upload for an
 * income source is saved to the source and then attached to its row. A pending entry lasts until the answers list
 * the file where its target shows it. Every batch runs as a mutation with INCOME_DOCUMENT_UPLOAD_KEY, so the wizard
 * can block navigation while one runs
 */
export function useIncomeDocumentUploads(applicationId: string, answers: UploadAnswers) {
  const queryClient = useQueryClient();
  const { mutateAsync: requestUserToken } = useCappsRenterDocumentsWebApiHandler_requestUserToken({
    meta: { context: { api: 'cappsRenterDocuments', details: 'requestUserToken' } },
  });
  const { mutateAsync: addIncomeDocuments } = useCappsRenterDocumentsWebApiHandler_addIncomeDocuments({
    meta: {
      context: { api: 'cappsRenterDocuments', details: 'addIncomeDocuments' },
      invalidates: [RENTAL_APPLICATION_TAG, DOCUMENT_POOL_TAG],
    },
  });
  const { mutateAsync: addRequiredDocuments } = useAddRequiredDocuments();
  const { mutateAsync: attachRequiredDocuments } = useAttachRequiredDocuments();
  const [pending, setPending] = useState<Array<PendingUpload>>([]);
  const [failed, setFailed] = useState<Array<FailedUpload>>([]);
  const [latestFailure, setLatestFailure] = useState<{ fileName: string; count: number }>();
  const [prunedForAnswers, setPrunedForAnswers] = useState(answers);
  const latestAnswers = useLatest(answers);
  const maxDocumentsPerIncome = getMaxDocumentsPerIncome(useIsBaseApplicationChangesEnabled());
  const managerRef = useRef<ZDocsUploadManager | null>(null);
  const claimedIdsRef = useRef(new Set<string>());
  const uploadActivityRef = useRef(() => {});

  if (answers !== prunedForAnswers) {
    setPrunedForAnswers(answers);
    setPending((current) => current.filter((entry) => !isListed(answers, entry)));
    setFailed((current) => current.filter((entry) => isOpenAttachFailure(answers, entry)));
  }

  useEffect(() => () => managerRef.current?.disconnect(), []);

  const getManager = () => {
    managerRef.current ??= new ZDocsUploadManager({
      generateUploadToken: async () => (await requestUserToken()).userToken ?? '',
      onChange: () => uploadActivityRef.current(),
      onProgress: () => uploadActivityRef.current(),
    });
    return managerRef.current;
  };

  // The manager has no timeout, so a batch with no upload activity fails, and the next batch gets a new manager
  const uploadFiles = (files: Array<File>) => {
    const manager = getManager();
    return new Promise<UploadedFiles>((resolve) => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      let isDone = false;
      const finish = (uploads: UploadedFiles) => {
        if (isDone) {
          return;
        }
        isDone = true;
        clearTimeout(timer);
        uploadActivityRef.current = () => {};
        resolve(uploads);
      };
      uploadActivityRef.current = () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          manager.disconnect();
          if (managerRef.current === manager) {
            managerRef.current = null;
          }
          finish([]);
        }, STALLED_UPLOAD_MS);
      };
      uploadActivityRef.current();
      manager.uploadFiles(files).then(finish, () => finish([]));
    });
  };

  const dropPending = (key: string) => setPending((current) => current.filter((entry) => entry.key !== key));

  const markSaved = (key: string, savedIds: Array<string>) => {
    savedIds.forEach((id) => claimedIdsRef.current.add(id));
    setPending((current) =>
      current.flatMap((entry) => {
        if (entry.key !== key) {
          return [entry];
        }
        const saved = { ...entry, savedIds };
        return isListed(latestAnswers.current, saved) ? [] : [saved];
      }),
    );
  };

  const addFailed = (entry: FailedUpload) => {
    setFailed((current) => [...current, entry]);
    setLatestFailure((current) => ({ fileName: entry.fileName, count: (current?.count ?? 0) + 1 }));
  };

  const markFailed = (target: UploadTarget, entry: Entry, reason: FailedUploadReason, details: FailureDetails) => {
    dropPending(entry.key);
    addFailed({ key: entry.key, file: entry.file, fileName: entry.fileName, target, reason, ...details });
  };

  const save = (target: UploadTarget, messageToken: string) =>
    target.kind === 'applicationRow'
      ? addRequiredDocuments({
          postData: { applicationId, documentType: target.documentType, documents: [{ messageToken }] },
        })
      : addIncomeDocuments({ postData: { incomeId: target.incomeId, documents: [{ messageToken }] } });

  const saveFile = async (target: UploadTarget, entry: Entry, messageToken: string) => {
    const knownIds = new Set([...getStoredIds(latestAnswers.current, target), ...claimedIdsRef.current]);
    let mayBeStored = true;
    let canRetry = true;
    try {
      const savedIds = ((await save(target, messageToken)) ?? []).map(({ documentId }) => documentId);
      if (savedIds.length > 0) {
        markSaved(entry.key, savedIds);
        return savedIds[0];
      }
      // capps dropped the token: an empty answer stores nothing
      mayBeStored = false;
    } catch (error) {
      // capps rejects a 4xx before it stores anything
      if (DatarouterError.is(error) && error.responseStatus >= 400 && error.responseStatus < 500) {
        mayBeStored = false;
        canRetry = false;
      }
    }
    // Every failure refetches before the card shows
    const application = await fetchLatestApplication(queryClient, applicationId).catch(() => undefined);
    // Saves run one at a time, so an unclaimed new id can only come from this save
    const storedId = mayBeStored
      ? [...getStoredIds(application?.answers, target)].find(
          (id) => !knownIds.has(id) && !claimedIdsRef.current.has(id),
        )
      : undefined;
    if (storedId) {
      markSaved(entry.key, [storedId]);
      return storedId;
    }
    markFailed(target, entry, 'save', { canRetry });
    return undefined;
  };

  const attachFile = async (target: IncomeRowTarget, entry: Entry, documentId: string) => {
    try {
      await attachRequiredDocuments({
        postData: {
          applicationId,
          documentType: target.documentType,
          incomeId: target.incomeId,
          documentIds: [documentId],
        },
      });
    } catch {
      // capps writes the link before it reads the file back, so a failed attach can still be stored
      const application = await fetchLatestApplication(queryClient, applicationId).catch(() => undefined);
      if (application && getAttachOutcome(application.answers, target, documentId) !== 'failed') {
        // Stored, or nothing to attach any more: a saved file still shows in Supporting
        dropPending(entry.key);
      } else {
        markFailed(target, entry, 'attach', { canRetry: true, documentId });
      }
    }
  };

  const uploadEntries = async (target: UploadTarget, entries: Array<Entry>) => {
    const uploads = await uploadFiles(entries.map(({ file }) => file));
    for (const entry of entries) {
      const upload = uploads.find(({ file }) => file === entry.file);
      if (!upload?.messageToken) {
        const isRefused = !!upload && isRefusedByZDocs(upload);
        markFailed(target, entry, isRefused ? 'rejected' : 'upload', { canRetry: !isRefused });
        continue;
      }
      // The saved file is attached before the next file is saved, so a later stall leaves no file unattached
      const documentId = await saveFile(target, entry, upload.messageToken);
      if (documentId && target.kind === 'incomeRow') {
        await attachFile(target, entry, documentId);
      }
    }
  };

  const { mutate: uploadBatch } = useMutation({
    mutationKey: INCOME_DOCUMENT_UPLOAD_KEY,
    scope: INCOME_DOCUMENT_UPLOAD_SCOPE,
    meta: { context: { api: 'cappsRenterDocuments', details: 'uploadIncomeDocuments' } },
    mutationFn: ({ target, entries }: { target: UploadTarget; entries: Array<Entry> }) =>
      uploadEntries(target, entries),
  });

  const { mutate: retryUpload } = useMutation({
    mutationKey: INCOME_DOCUMENT_UPLOAD_KEY,
    scope: INCOME_DOCUMENT_UPLOAD_SCOPE,
    meta: { context: { api: 'cappsRenterDocuments', details: 'retryIncomeDocumentUpload' } },
    mutationFn: async (entry: FailedUpload) => {
      // A retry never writes before this refetch. If the refetch fails, the card comes back as it was
      const application = await fetchLatestApplication(queryClient, applicationId).catch(() => undefined);
      if (!application) {
        dropPending(entry.key);
        setFailed((current) => [...current, entry]);
        return;
      }
      const { target } = entry;
      if (entry.reason === 'attach' && target.kind === 'incomeRow' && entry.documentId) {
        if (getAttachOutcome(application.answers, target, entry.documentId) === 'failed') {
          await attachFile(target, entry, entry.documentId);
        } else {
          dropPending(entry.key);
        }
        return;
      }
      // The manager skips a File it already uploaded, so a retry uploads a copy
      const copy = new File([entry.file], entry.file.name, {
        type: entry.file.type,
        lastModified: entry.file.lastModified,
      });
      await uploadEntries(target, [{ key: entry.key, file: copy, fileName: entry.fileName }]);
    },
  });

  const getRejection = (file: File, room: number): FailedUploadReason | undefined => {
    if (!isAcceptedFile(file)) {
      return 'type';
    }
    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      return 'tooLarge';
    }
    return room === 0 ? 'limit' : undefined;
  };

  // No source cap for application-level rows
  const getRoom = (target: UploadTarget) => {
    const incomeId = getIncomeId(target);
    return incomeId === undefined
      ? Number.POSITIVE_INFINITY
      : Math.max(maxDocumentsPerIncome - getHeldDocumentCount(answers, pending, incomeId), 0);
  };

  const addFiles = useMemoizedFn((target: UploadTarget, files: Array<File>) => {
    let room = getRoom(target);
    const accepted: Array<Entry> = [];
    for (const file of files) {
      const entry = { key: `upload-${(nextKey += 1)}`, file, fileName: file.name.replace(/\.[^.]+$/, '') };
      const rejection = getRejection(file, room);
      if (rejection) {
        addFailed({ ...entry, target, reason: rejection, canRetry: false });
      } else {
        room -= 1;
        accepted.push(entry);
      }
    }
    if (accepted.length > 0) {
      setPending((current) => [...current, ...accepted.map(({ key, fileName }) => ({ key, target, fileName }))]);
      uploadBatch({ target, entries: accepted });
    }
  });

  const retry = useMemoizedFn((key: string) => {
    const entry = failed.find((current) => current.key === key);
    if (!entry) {
      return;
    }
    // An attach adds no file to the source, so only an upload or save retry needs room
    if (entry.reason !== 'attach' && getRoom(entry.target) === 0) {
      setFailed((current) =>
        current.map((item) => (item.key === key ? { ...item, reason: 'limit', canRetry: false } : item)),
      );
      return;
    }
    setFailed((current) => current.filter((item) => item.key !== key));
    setPending((current) => [
      ...current,
      {
        key,
        target: entry.target,
        fileName: entry.fileName,
        savedIds: entry.reason === 'attach' && entry.documentId ? [entry.documentId] : undefined,
      },
    ]);
    retryUpload(entry);
  });

  const dismiss = useMemoizedFn((key: string) => setFailed((current) => current.filter((item) => item.key !== key)));

  const hasRoom = (target: UploadTarget) => getRoom(target) > 0;

  return { pending, failed, latestFailure, addFiles, hasRoom, retry, dismiss };
}

export type IncomeDocumentUploads = ReturnType<typeof useIncomeDocumentUploads>;
